import React, { useCallback, useEffect, useState, useRef } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";

const MultiRangeSlider = ({ min, max, value, onChange }) => {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const minValRef = useRef(null);
  const maxValRef = useRef(null);
  const range = useRef(null);

  const getPercent = useCallback(
    (val) => {
      if (max <= min) return 0;
      return Math.round(
        Math.min(100, Math.max(0, ((val - min) / (max - min)) * 100))
      );
    },
    [min, max]
  );

  // Update range bar position and width
  useEffect(() => {
    if (range.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(maxVal);
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, maxVal, getPercent]);

  // Sync with external filter state
  useEffect(() => {
    if (value && Object.keys(value).length !== 0) {
      setMinVal(value.value.min);
      setMaxVal(value.value.max);
    } else {
      // Reset to original min/max when filter is cleared
      setMinVal(min);
      setMaxVal(max);
    }
  }, [value, min, max]);

  const emitChange = useCallback(() => {
    const currentMin = minValRef.current ? +minValRef.current.value : minVal;
    const currentMax = maxValRef.current ? +maxValRef.current.value : maxVal;
    onChange({ min: currentMin, max: currentMax });
  }, [minVal, maxVal, onChange]);

  const formatPrice = (val) =>
    Number(val).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });

  return (
    <>
      <div className="slider__values">
        <span className="slider__left-value">{formatPrice(minVal)}</span>
        <span className="slider__right-value">{formatPrice(maxVal)}</span>
      </div>

      <div className="range-slider-container">

        <input
          type="range"
          min={min}
          max={max}
          value={minVal}
          ref={minValRef}
          onChange={(event) => {
            const val = Math.min(+event.target.value, maxVal - 1);
            setMinVal(val);
            event.target.value = val.toString();
          }}
          onMouseUp={emitChange}
          onTouchEnd={emitChange}
          onKeyUp={emitChange}
          className={classnames("thumb thumb--zindex-3", {
            "thumb--zindex-5": minVal > max - 100,
          })}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={maxVal}
          ref={maxValRef}
          onChange={(event) => {
            const val = Math.max(+event.target.value, minVal + 1);
            setMaxVal(val);
            event.target.value = val.toString();
          }}
          onMouseUp={emitChange}
          onTouchEnd={emitChange}
          onKeyUp={emitChange}
          className="thumb thumb--zindex-4"
        />

        <div className="slider">
          <div className="slider__track" />
          <div ref={range} className="slider__range" />
        </div>


      </div>
    </>
  );
};

MultiRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default MultiRangeSlider;
